import { Injectable, isDevMode } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ApiService } from '../http/api.service';

export type WhatsAppConsentSource = 'onboarding' | 'setup_prompt' | 'profile_change' | 'hobbies_profile';

export interface ContactStatus {
  country_iso2: string | null;
  whatsapp_e164_masked: string | null;
  whatsapp_verified: boolean;
  whatsapp_opt_in: boolean;
  needs_setup: boolean;
  legacy_whatsapp_e164: string | null;
}

export interface StartVerificationRequest {
  country_iso2: string;
  phone: string;
  consent: true;
  source: WhatsAppConsentSource;
}

export interface StartVerificationResponse {
  verification_id: string;
  e164_masked: string;
  expires_at: string;
  resend_after: string;
}

export interface ResendVerificationResponse {
  resend_after: string;
  expires_at: string;
}

export interface ConfirmVerificationResponse {
  whatsapp_e164_masked: string;
  verified_at: string;
}

export type ContactErrorCode =
  | 'INVALID_PHONE'
  | 'CONSENT_REQUIRED'
  | 'WHATSAPP_NUMBER_IN_USE'
  | 'OTP_RATE_LIMITED'
  | 'OTP_INVALID'
  | 'OTP_EXPIRED'
  | 'OTP_LOCKED'
  | 'UNKNOWN';

export interface ContactError {
  code: ContactErrorCode;
  message: string;
  attemptsLeft?: number;
  retryAfterSeconds?: number;
}

export const WHATSAPP_CONSENT_TEXT =
  'Contemplate may message me on WhatsApp from its Hobbies and Mind Fitness numbers. Reply STOP to opt out.';

const ERROR_MESSAGES: Record<Exclude<ContactErrorCode, 'UNKNOWN'>, string> = {
  INVALID_PHONE: "That doesn't look like a valid mobile number for the selected country.",
  CONSENT_REQUIRED: 'Please tick the box to allow WhatsApp messages.',
  WHATSAPP_NUMBER_IN_USE:
    "This number is already linked to another Contemplate account. Contact support if it's yours.",
  OTP_RATE_LIMITED: 'Too many codes requested. Please wait a little before trying again.',
  OTP_INVALID: "That code isn't right. Please check it and try again.",
  OTP_EXPIRED: 'That code has expired. Request a new one.',
  OTP_LOCKED: 'Too many wrong attempts. Request a new code or change the number.'
};

/** Dev-only mock (no API needed). Enable with localStorage `contemplate.mockContact = '1'` in non-production builds. */
const MOCK_FLAG_KEY = 'contemplate.mockContact';

/**
 * Mock behaviour (for manual testing):
 *  - phone ending 000 -> INVALID_PHONE; 111 -> WHATSAPP_NUMBER_IN_USE; 222 -> OTP_RATE_LIMITED
 *  - code 000000 -> OTP_LOCKED path after attempts; 111111 -> OTP_EXPIRED; 123456 -> success; anything else -> OTP_INVALID
 */
@Injectable({ providedIn: 'root' })
export class ContactService {
  private mockAttempts = 5;
  private mockState: ContactStatus = {
    country_iso2: 'IN',
    whatsapp_e164_masked: null,
    whatsapp_verified: false,
    whatsapp_opt_in: false,
    needs_setup: true,
    legacy_whatsapp_e164: '+919876543210'
  };

  constructor(private api: ApiService) {}

  get mockEnabled(): boolean {
    // Never honour the flag in production builds.
    if (!isDevMode()) return false;
    try {
      return localStorage.getItem(MOCK_FLAG_KEY) === '1';
    } catch {
      return false;
    }
  }

  getStatus(): Observable<ContactStatus> {
    return this.getStatusRaw().pipe(map(res => unwrap<ContactStatus>(res)));
  }

  private getStatusRaw(): Observable<any> {
    if (this.mockEnabled) return this.mockDelay(() => ({ ...this.mockState }));
    return this.api.get<ContactStatus>('user/contact/status');
  }

  startVerification(body: StartVerificationRequest): Observable<StartVerificationResponse> {
    return this.startVerificationRaw(body).pipe(map(res => unwrap<StartVerificationResponse>(res)));
  }

  private startVerificationRaw(body: StartVerificationRequest): Observable<any> {
    if (this.mockEnabled) {
      return this.mockDelay(() => {
        if (body.phone.endsWith('000')) throw this.mockError(400, 'INVALID_PHONE');
        if (body.phone.endsWith('111')) throw this.mockError(409, 'WHATSAPP_NUMBER_IN_USE');
        if (body.phone.endsWith('222')) throw this.mockError(429, 'OTP_RATE_LIMITED', { retry_after: 45 });
        this.mockAttempts = 5;
        return {
          verification_id: 'mock-verification',
          e164_masked: `${body.phone.slice(0, 3)}••••••${body.phone.slice(-2)}`,
          expires_at: new Date(Date.now() + 10 * 60_000).toISOString(),
          resend_after: new Date(Date.now() + 20_000).toISOString()
        };
      });
    }
    return this.api.post<StartVerificationResponse>('user/contact/whatsapp/verifications', body);
  }

  resend(verificationId: string): Observable<ResendVerificationResponse> {
    return this.resendRaw(verificationId).pipe(map(res => unwrap<ResendVerificationResponse>(res)));
  }

  private resendRaw(verificationId: string): Observable<any> {
    if (this.mockEnabled) {
      return this.mockDelay(() => ({
        resend_after: new Date(Date.now() + 20_000).toISOString(),
        expires_at: new Date(Date.now() + 10 * 60_000).toISOString()
      }));
    }
    return this.api.post<ResendVerificationResponse>(
      `user/contact/whatsapp/verifications/${encodeURIComponent(verificationId)}/resend`
    );
  }

  confirm(verificationId: string, code: string): Observable<ConfirmVerificationResponse> {
    return this.confirmRaw(verificationId, code).pipe(map(res => unwrap<ConfirmVerificationResponse>(res)));
  }

  private confirmRaw(verificationId: string, code: string): Observable<any> {
    if (this.mockEnabled) {
      return this.mockDelay(() => {
        if (code === '111111') throw this.mockError(410, 'OTP_EXPIRED');
        if (code === '123456') {
          this.mockState = { ...this.mockState, whatsapp_verified: true, needs_setup: false, whatsapp_e164_masked: '+91••••••10' };
          return { whatsapp_e164_masked: '+91••••••10', verified_at: new Date().toISOString() };
        }
        this.mockAttempts -= 1;
        if (this.mockAttempts <= 0) throw this.mockError(423, 'OTP_LOCKED');
        throw this.mockError(400, 'OTP_INVALID', { attempts_left: this.mockAttempts });
      });
    }
    return this.api.post<ConfirmVerificationResponse>(
      `user/contact/whatsapp/verifications/${encodeURIComponent(verificationId)}/confirm`,
      { code }
    );
  }

  setCountry(countryIso2: string): Observable<{ country_iso2: string }> {
    return this.setCountryRaw(countryIso2).pipe(map(res => unwrap<{ country_iso2: string }>(res)));
  }

  private setCountryRaw(countryIso2: string): Observable<any> {
    if (this.mockEnabled) return this.mockDelay(() => { this.mockState = { ...this.mockState, country_iso2: countryIso2 }; return { country_iso2: countryIso2 }; });
    return this.api.put<{ country_iso2: string }>('user/contact/country', { country_iso2: countryIso2 });
  }

  removeWhatsApp(): Observable<unknown> {
    return this.removeWhatsAppRaw().pipe(map(res => unwrap<unknown>(res)));
  }

  private removeWhatsAppRaw(): Observable<any> {
    if (this.mockEnabled) return this.mockDelay(() => ({}));
    return this.api.delete<unknown>('user/contact/whatsapp');
  }

  /** Normalises any thrown HTTP error into a friendly, typed error. */
  toContactError(err: unknown): ContactError {
    const http = err as Partial<HttpErrorResponse> | undefined;
    const body: any = http?.error ?? {};
    const raw = body?.code ?? body?.error?.code ?? body?.error;
    const code: ContactErrorCode =
      typeof raw === 'string' && raw in ERROR_MESSAGES ? (raw as ContactErrorCode) : this.codeFromStatus(http?.status);
    const attemptsLeft = numberOrUndefined(body?.attempts_left ?? body?.details?.attempts_left);
    const retryAfterSeconds = numberOrUndefined(body?.retry_after ?? body?.details?.retry_after);
    if (code === 'UNKNOWN') {
      return {
        code,
        message:
          http?.status === 0
            ? "We couldn't reach the server. Check your connection and try again."
            : 'Something went wrong. Please try again.'
      };
    }
    return { code, message: ERROR_MESSAGES[code], attemptsLeft, retryAfterSeconds };
  }

  private codeFromStatus(status?: number): ContactErrorCode {
    // Only statuses that map to exactly one code in the contract.
    if (status === 423) return 'OTP_LOCKED';
    if (status === 410) return 'OTP_EXPIRED';
    if (status === 429) return 'OTP_RATE_LIMITED';
    return 'UNKNOWN';
  }

  private mockError(status: number, code: string, extra: Record<string, unknown> = {}): HttpErrorResponse {
    return new HttpErrorResponse({ status, error: { code, ...extra } });
  }

  private mockDelay<T>(fn: () => T): Observable<T> {
    return timer(500).pipe(
      switchMap(() => {
        try {
          return of({ status: 'success', data: fn() } as unknown as T);
        } catch (e) {
          return throwError(() => e);
        }
      }),
    );
  }
}

/** API responses are `{ status, data }`; tolerate the bare payload too. */
function unwrap<T>(res: any): T {
  return res && typeof res === 'object' && 'data' in res && 'status' in res ? res.data : res;
}

function numberOrUndefined(v: unknown): number | undefined {
  const n = Number(v);
  return v !== undefined && v !== null && Number.isFinite(n) ? n : undefined;
}
